import { useRef, useCallback, memo } from 'react';
import {
  CELL_WIDTH,
  MILESTONE_HORIZONTAL_OFFSET,
  EVENT_BAR_ROW_HEIGHT,
  MILESTONE_TOP_WITH_EVENTS_OFFSET
} from '../constants/layout';
import { getIconUnicode, ENTITY_TYPE, DRAG_ACTION } from '../constants/types';
import useDraggable from '../hooks/useDraggable';
import useTooltip from '../hooks/useTooltip';
import Tooltip from './Tooltip';
import './Milestone.scss';

/**
 * Renders a milestone marker on a specific day in the calendar.
 * Displays as a colored circle with an icon symbol.
 * Supports drag-to-move and click-to-edit interactions.
 * 
 * @param {Object} props
 * @param {Object} props.milestone - Milestone data
 * @param {string} props.milestone.id - Unique milestone identifier
 * @param {string} props.milestone.title - Milestone title
 * @param {string} props.milestone.date - Date (YYYY-MM-DD)
 * @param {string} [props.milestone.icon='flag'] - Icon type: 'flag', 'diamond', 'star', 'circle'
 * @param {string} [props.milestone.color] - Background color
 * @param {string} [props.milestone.description] - Description for tooltip
 * @param {boolean} [props.milestone.locked] - Whether milestone is locked (no dragging)
 * @param {number} props.column - Column index in the grid
 * @param {number} [props.row=-1] - Vertical stacking row (-1 = no events above)
 * @param {boolean} [props.isHidden=false] - Hide milestone (during drag of original)
 * @param {boolean} [props.isPreview=false] - Render as drag preview (no interactions)
 * @param {Function} [props.onClick] - Handler for click (milestone) => void
 * @param {Function} [props.onDragStart] - Handler for drag start (dragData, mouseEvent) => void
 * @param {Function} [props.onDragEnd] - Handler for drag end (mouseup anywhere) => void
 */
const Milestone = memo(function Milestone({ milestone, column, row = -1, isHidden = false, isPreview = false, onClick, onDragStart, onDragEnd }) {
  const milestoneRef = useRef(null);
  
  const icon = getIconUnicode(milestone.icon);
  
  // Calculate positioning
  // Horizontal: center milestone in cell
  // Vertical: row-based positioning
  const leftPosition = column * CELL_WIDTH + MILESTONE_HORIZONTAL_OFFSET;
  const topPosition = row * EVENT_BAR_ROW_HEIGHT + MILESTONE_TOP_WITH_EVENTS_OFFSET;
  
  const style = {
    left: `${leftPosition}px`,
    top: `${topPosition}px`,
    backgroundColor: milestone.color || '#333'
  };

  // Build drag data for milestone
  const buildDragData = useCallback(() => {
    // Block drag if read-only
    if (milestone.isReadOnly) {
      return null;
    }
    
    // Block drag if locked, but allow click to open sidebar
    if (milestone.locked) {
      return null;
    }
    
    // Set drag data
    return {
      type: ENTITY_TYPE.MILESTONE,
      id: milestone.id,
      action: DRAG_ACTION.MOVE,
      date: milestone.date
    };
  }, [milestone]);

  // Use draggable hook
  const { 
    isDragging, 
    wasDraggingRef,
    handlePotentialDrag
  } = useDraggable({
    onDragStart,
    onDragEnd,
    buildDragData
  });

  const handleClick = (e) => {
    // Don't fire click if we just finished dragging
    if (wasDraggingRef.current) {
      return;
    }
    e.stopPropagation();
    if (onClick) {
      onClick(milestone);
    }
  };

  // Use tooltip hook
  const { tooltipProps, handleMouseEnter, handleMouseLeave } = useTooltip({
    title: milestone.title,
    description: milestone.description,
    disabled: isDragging
  });

  const isLocked = milestone.locked;

  const classNames = [
    'milestone',
    isDragging ? 'milestone--dragging' : '',
    isHidden ? 'milestone--hidden' : '',
    isPreview ? 'milestone--preview' : '',
    isLocked ? 'milestone--locked' : '',
    milestone.isFromSharedProject ? 'milestone--shared' : ''
  ].filter(Boolean).join(' ');

  // Preview milestones don't need interaction handlers
  if (isPreview) {
    return (
      <div 
        className={classNames}
        style={style}
      >
        <span className="milestone__icon">{icon}</span>
      </div>
    );
  }

  return (
    <div 
      ref={milestoneRef}
      className={classNames}
      style={style}
      onClick={handleClick}
      onMouseDown={handlePotentialDrag}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <span className="milestone__icon">{icon}</span>
      {/* Lock icon indicator */}
      {isLocked && <span className="milestone__lock-icon">🔒</span>}
      {/* Tooltip component */}
      <Tooltip {...tooltipProps} />
    </div>
  );
});

export default Milestone;
